/**
 * ContactPickerDialog — choose a saved Contact Book contact to populate a
 * document-sign recipient row.
 *
 * Only contacts that have an email address are selectable (a recipient needs
 * one); the contact's first email is used. Picking a contact hands its name +
 * email back to the caller and closes the dialog.
 */

import { useMemo, useState } from 'react';
import { Loader2, Search, User } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface PickedContact {
  name: string;
  email: string;
}

interface ContactPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (contact: PickedContact) => void;
}

interface ContactRow {
  id: string;
  name: string;
  email: string;
}

/** Best-effort display name: full name, falling back to nickname or email. */
function contactDisplayName(
  firstName: string,
  lastName: string,
  nickname: string,
  email: string,
): string {
  const full = [firstName, lastName]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' ');
  return full || nickname.trim() || email;
}

export function ContactPickerDialog({ open, onOpenChange, onPick }: ContactPickerDialogProps) {
  const [search, setSearch] = useState('');

  const contactsQuery = useQuery({
    queryKey: queryKeys.contactBook.list({ scope: 'document-sign-recipient', status: 'active' }),
    queryFn: () => api.listContacts({ status: 'active', sortBy: 'name', limit: 200 }),
    enabled: open,
    select: (data): ContactRow[] =>
      data.contacts
        .map((c) => {
          const email = c.emails.find((e) => e.value.trim())?.value.trim() ?? '';
          return {
            id: c.id,
            name: contactDisplayName(c.firstName, c.lastName, c.nickname, email),
            email,
          };
        })
        .filter((c) => c.email !== ''),
  });

  const filtered = useMemo(() => {
    const rows = contactsQuery.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q),
    );
  }, [contactsQuery.data, search]);

  function handlePick(c: ContactRow) {
    onPick({ name: c.name, email: c.email });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-md flex-col">
        <DialogHeader>
          <DialogTitle>Add a contact</DialogTitle>
          <DialogDescription>
            Pick a saved contact to add as a recipient. Contacts without an email aren&apos;t shown.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contacts"
            className="pl-8"
            aria-label="Search contacts"
          />
        </div>

        <div className="h-72 space-y-1 overflow-y-auto pr-1">
          {contactsQuery.isLoading ? (
            <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading contacts…
            </p>
          ) : contactsQuery.isError ? (
            <p className="py-6 text-sm text-destructive">Could not load contacts. Try again.</p>
          ) : filtered.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">
              {search.trim() ? 'No contacts match your search.' : 'No contacts with an email yet.'}
            </p>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => handlePick(c)}
                className="flex w-full items-center gap-3 rounded-md border border-transparent px-3 py-2 text-left text-sm transition-colors hover:border-border hover:bg-muted/50"
              >
                <User className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-foreground">{c.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{c.email}</span>
                </span>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
